import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Slide from '@mui/material/Slide';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import { AxiosErrorTitle } from 'components/CallServer/AxiosHandler';
import CardHeaderColor from 'components/MuiExtras/Card/CardHeaderColor';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import HtmlUtils from 'lib/HtmlUtils';
import { ApiException } from 'models/ApiModels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import ButtonClose from '../Buttons/ButtonClose';

/////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////


const styles = (theme: Theme) => createStyles({
    modal: {
        alignItems: 'center',
        justifyContent: 'center',
        display: "flex",
        width: "100%",
        height: "100%",
    },
    card: {
        minWidth: 400,
        maxWidth: 600,
    }
});

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    error: any;
    open: boolean;
    onClose: () => void;
}

type Props = IProps & WithStyles<typeof styles>;

/////////////////////////////////////////////////////////////////////////////////////////////////

const RequestErrorDialog: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const { classes, error } = props;

    if (error == null)
        return null;

    //////////////////////////////////////////

    if (error.hasOwnProperty("handled") && error["handled"] === true)
        return null;

    //////////////////////////////////////////

    const handledError: ApiException =
        error?.hasOwnProperty("handledError")
            ? error["handledError"]
            : error?.hasOwnProperty("exceptionMessage")
                ? error
                : null;

    const debug = HtmlUtils.isLocalhost;

    const detailMessage = handledError
        ? (!debug && handledError.isFatal ? null : handledError.exceptionMessage)
        : error.hasOwnProperty("message")
            ? error.message
            : error.toString;

    const stack = debug && handledError?.isFatal === true
        ? handledError.stackTrace
        : null;

    const traceIdentifier = handledError?.isFatal === true
        ? handledError.traceIdentifier
        : null;

    const message =
        handledError
            && handledError.errorList
            && Array.isArray(handledError.errorList)
            && (handledError.errorList as Array<any>).length > 0
            ? (
                <ul>
                    {
                        handledError.errorList.map((v) => {
                            return <li>{v.message}</li>;
                        })
                    }
                </ul>
            )
            : (
                <div>
                    <MuiContainer>
                        <b>{t("Components.RequestErrorDialog.Title")}</b>
                        <MuiColor variant="mutedDark">{detailMessage && <p>{detailMessage}</p>}</MuiColor>
                    </MuiContainer>

                    {
                        traceIdentifier &&
                        <MuiContainer margin="dense">
                            <MuiColor variant="muted">
                                <small>{t("Components.RequestErrorDialog.TrackerId")}: <b>{traceIdentifier}</b></small>
                            </MuiColor>
                        </MuiContainer>
                    }

                    {
                        stack &&
                        <details>
                            <summary>Stack Trace</summary>
                            <code>{stack}</code>
                        </details>
                    }
                </div>
            );

    //////////////////////////////////////////

    return (
        <Dialog
            role="dialog"
            className={classes.modal}
            style={{ zIndex: 20001 }}
            open={props.open}
            onClose={() => props.onClose()}
            TransitionComponent={Slide}
            TransitionProps={
                {
                    //direction: "up",
                    in: true,
                    mountOnEnter: true,
                    unmountOnExit: true,
                }
            }
        >
            <DialogTitle style={{ padding: 0, marginBottom: 20 }}>
                <CardHeaderColor color="error" title={<AxiosErrorTitle exceptionType={handledError?.exceptionType} />} />
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1">
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions style={{ justifyContent: "center" }}>
                <ButtonClose onClick={() => props.onClose()} autoFocus />
            </DialogActions>
        </Dialog>
    );
};

export default withStyles(styles)(RequestErrorDialog);